import React from 'react'

import {
  FinanceFinancialDocumentAssociationInput,
  FinanceValueObjectsInvoiceStatusEnum,
  JournalEntryFragment,
  JournalLineReconciliationFragment,
  TaxTypeFragment,
  UpdateFinanceInvoiceMutationVariables,
} from 'generated/resources/graphql'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useForm } from 'modules/core/hooks/useForm'
import { fromCents, toCents } from 'modules/core/lib/formatCurrencyUtils'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

import { Tax } from 'modules/expenses/hooks/useExpensesCustomization'
import { InvoiceDetail } from 'modules/finance/components/Invoices/Detail/Content/Form'
import { buildDefaultValues } from 'modules/finance/components/Invoices/Detail/Content/Form/_utils'
import { InvoiceForm } from 'modules/finance/components/Invoices/Detail/Content/Form/types'
import {
  FinancialDocumentAssociation,
  InvoiceType,
  LegalEntity,
} from 'modules/finance/components/Invoices/Detail/Content/Form/types'
import { SendToPayModal } from 'modules/finance/components/Invoices/SendToPayModal'
import { FailedVendorData } from 'modules/finance/components/Invoices/SendToPayModal/types'
import { useUpdate } from 'modules/finance/hooks/useInvoices'

interface ContentProps<T extends InvoiceType> {
  invoice: T
  onClose: () => void
  showSendToPayButton?: boolean
  showReconciliationSection?: boolean
  showExpense?: boolean
  showSoftware?: boolean
  showProjects?: boolean
  showPurchaseOrder?: boolean
  legalEntities: LegalEntity[]
  taxTypes?: TaxTypeFragment[]
  canManageJournalEntries: boolean
  canReconcileWithJournalLines: boolean
  canReconcileWithFinancialDocuments: boolean
  journalLines: JournalLineReconciliationFragment[]
  journalEntries: JournalEntryFragment[]
}

export const InvoiceDetailContent = <T extends InvoiceType>({
  invoice,
  onClose,
  showSendToPayButton = false,
  showReconciliationSection = false,
  showExpense = false,
  showSoftware = false,
  showProjects = false,
  showPurchaseOrder = false,
  legalEntities,
  taxTypes,
  canManageJournalEntries,
  canReconcileWithJournalLines,
  canReconcileWithFinancialDocuments,
  journalLines,
  journalEntries,
}: ContentProps<T>) => {
  const { company } = useCurrentSession()
  const invoiceId = invoice.id
  const { update } = useUpdate(String(invoiceId), company.id)
  const [showSendToPayModal, setShowSendToPayModal] = React.useState<boolean>(false)

  const allAssociations = [
    invoice.purchaseOrderFinancialDocumentAssociation,
    invoice.softwareSetupFinancialDocumentAssociation,
    invoice.projectsFinancialDocumentAssociation,
    invoice.subprojectsFinancialDocumentAssociation,
  ].filter((association) => association !== null && association !== undefined)

  const sortedAssociations = allAssociations.sort((a, b) => a.id.localeCompare(b.id, undefined, { numeric: true }))

  const lastPosition = sortedAssociations.length - 1
  const validAssociations =
    sortedAssociations[lastPosition]?.resourceType === 'subproject'
      ? sortedAssociations
          .filter(
            (association) =>
              association?.resourceType === 'project' || association?.resourceType === 'subproject'
          )
          .sort((a, b) => {
            if (a.resourceType === 'project' && b.resourceType === 'subproject') return 1
            if (a.resourceType === 'subproject' && b.resourceType === 'project') return -1
            return 0
          })
      : [sortedAssociations[lastPosition]]

  const associatedResources: FinancialDocumentAssociation[] = validAssociations
    .filter((association): association is NonNullable<typeof association> => association != null)
    .map((association) => ({
      id: association.id,
      resource: { id: association.resourceId, type: association.resourceType },
    }))

  const defaultValues = {
    ...buildDefaultValues({
      invoice,
      legalEntity: invoice.legalEntity,
      ledgerAccountId: invoice.accountResource?.account?.id,
    }),
    associations: associatedResources,
  }

  const form = useForm<InvoiceForm>({
    defaultValues,
    onSubmit: async (data) => {
      return await onSubmit(data, data.status)
    },
  })
  const updatevaluesFromForm = (values: InvoiceForm): UpdateFinanceInvoiceMutationVariables => {
    const mapTaxesFromForm = (taxes: Tax[]) =>
      taxes
        .map(({ base_amount, percentage, amount, type, tax_type_id, tax_type_name, tax_rates }) => {
          if (base_amount == null || amount == null || percentage == null) return null
          return {
            baseAmountCents: base_amount,
            percentage: percentage != null ? Number(fromCents(percentage).toFixed(4)) : null,
            amountCents: amount,
            type,
            taxTypeId: tax_type_id != '0' ? tax_type_id : null,
            taxTypeName: tax_type_name,
            taxRates: tax_rates,
          }
        })
        .filter(Boolean) ?? []

    const financialDocumentAssociations: FinanceFinancialDocumentAssociationInput[] | undefined =
      values.associations?.flatMap((association) =>
        association.resource
          ? [
              {
                id: association.id,
                resource: association.resource,
              },
            ]
          : []
      )

    const baseValues: UpdateFinanceInvoiceMutationVariables = {
      companyId: company.id,
      id: invoiceId,
      documentNumber: values.documentNumber,
      totalAmountCents: values.totalAmountCents
        ? toCents(Number(values.totalAmountCents))
        : undefined,
      currency: values.currency,
      documentDate: values.documentDate,
      taxes: mapTaxesFromForm(values.taxes),
      dueDate: values.dueDate,
      status: values.status,
      legalEntityId: values.legalEntity?.id,
      associations: financialDocumentAssociations,
      ledgerAccountId: values.ledgerAccountId,
      validatedAt: values.validatedAt,
      documentType: values.documentType,
      parentFinancialDocumentId:
        values.documentType === 'invoice' ? null : values.parentFinancialDocument?.id,
      vendorId: values.vendor?.id,
    }

    return baseValues
  }

  const isVendorValidForPayment = () => {
    return invoice.vendor?.iban && invoice.vendor?.legalName
  }

  const invalidInvoiceVendors = () => {
    if (!invoice.vendor?.id) return []

    if (isVendorValidForPayment()) {
      return []
    }
    const failedVendor: FailedVendorData = {
      invoicesCounter: 1,
      vendorId: invoice.vendor?.id,
      vendorDescription: invoice.vendor?.legalName || invoice.vendor?.iban || 'unknown',
    }
    return [failedVendor]
  }

  const onSubmit = async (values: InvoiceForm, status: FinanceValueObjectsInvoiceStatusEnum) => {
    if (status === 'sent_to_pay' && !isVendorValidForPayment()) {
      setShowSendToPayModal(true)
      const successResult = { success: true, data: null } satisfies { success: true; data: unknown }
      return Promise.resolve(successResult)
    } else {
      return onUpdate(values)
    }
  }

  const hasOnlyErrorsKey = (errors: Record<string, string>) => {
    const keys = Object.keys(errors)
    return keys.length === 1 && keys[0] === 'errors'
  }

  const onUpdate = async (values: InvoiceForm) => {
    const result = await update(updatevaluesFromForm(values))
    const handled = handleResult(result, (errors) => {
      if (!hasOnlyErrorsKey(errors)) {
        // in case of generic errors like the vendor one, we dont want the form to become invalid
        form.setErrors({
          ...errors,
          legalEntity: errors.legalEntityId,
          documentDate: errors.documentDate,
          documentNumber: errors.invoiceNumber,
        })
      }

      app.setFlash({
        type: 'wrong',
        message: errors.messages || errors.errors || i18n.t('errors.generic_error'),
      })
    })
    if (!handled.success) return handled

    app.setFlash({
      type: 'correct',
      message: i18n.t('finance.invoices.detail.success_save'),
    })
    onClose()

    return handled
  }

  const closeSendToPayModal = () => setShowSendToPayModal(false)

  return (
    <>
      {showSendToPayModal && (
        <SendToPayModal
          onClose={closeSendToPayModal}
          onSuccess={closeSendToPayModal}
          validInvoiceIds={invoiceId && isVendorValidForPayment() ? [invoiceId] : []}
          failedVendors={invalidInvoiceVendors()}
        />
      )}
      <InvoiceDetail
        legalEntities={legalEntities}
        invoice={invoice}
        form={form}
        showReconciliationSection={showReconciliationSection}
        canManageJournalEntries={canManageJournalEntries}
        showSoftware={showSoftware}
        showPurchaseOrder={showPurchaseOrder}
        showExpense={showExpense}
        showProjects={showProjects}
        showSendToPayButton={showSendToPayButton}
        taxTypes={taxTypes}
        canReconcileWithJournalLines={canReconcileWithJournalLines}
        canReconcileWithFinancialDocuments={canReconcileWithFinancialDocuments}
        journalLines={journalLines}
        journalEntries={journalEntries}
      />
    </>
  )
}
